import axios from 'axios';
import config from '../config/config';
import { STORAGE_KEYS, HTTP_STATUS, ERROR_MESSAGES } from '../constants/constants';

class ApiService {
  constructor() {
    // Token refresh state — prevents multiple simultaneous refresh calls
    this._isRefreshing = false;
    this._refreshQueue = []; // callbacks waiting for refresh to complete

    // Create axios instance with default config
    const TIMEOUT =
  parseInt(process.env.REACT_APP_API_TIMEOUT, 10) || 180000; // 3 min — set REACT_APP_API_TIMEOUT in .env to override
    this.api = axios.create({
      baseURL: config.apiBaseURL,
      timeout: TIMEOUT,
      withCredentials: true, // Important for HTTP-only cookies
      headers: {
        'Content-Type': 'application/json',
      },
    });
    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        // Skip adding auth header for login endpoints
        const isAuthEndpoint = config.url?.includes('/auth/') &&
          (config.url?.includes('/login') || config.url?.includes('/health'));

        if (!isAuthEndpoint) {
          const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }

        // Add request timestamp for debugging
        // if (config.debug) {
        //   console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, {
        //     data: config.data,
        //     params: config.params,
        //     headers: config.headers,
        //   });
        // }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => {
        // if (config.debug) {
        //   console.log(`[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`, {
        //     status: response.status,
        //     data: response.data,
        //   });
        // }

        return response;
      },
      (error) => {
        return this.handleResponseError(error);
      }
    );
  }

  handleResponseError(error) {
    if (config.debug) {
      //console.error('[API Error]', error);
    }

    // ── Stale refresh lock guard ──────────────────────────────────────────
    // On corporate/proxy networks the /auth/refresh call can hang indefinitely.
    // If _isRefreshing has been true for >12s, assume the refresh is dead,
    // reset the lock, reject everything in the queue, and force logout.
    // Without this, every request queued behind a hung refresh waits forever,
    // keeping isLoading=true and the app frozen.
    if (this._isRefreshing && this._refreshStartedAt) {
      const elapsed = Date.now() - this._refreshStartedAt;
      if (elapsed > 12000) {
        //console.warn('[API] Refresh lock timeout — forcing logout');
        this._isRefreshing = false;
        this._refreshStartedAt = null;
        this._refreshQueue.forEach(({ reject }) => reject(new Error('Refresh timeout')));
        this._refreshQueue = [];
        this._forceLogout('session_expired');
      }
    }

    const { response, request, message } = error;

    if (response) {
      const { status, data } = response;
      const backendMessage = data?.message || data?.error;

      // ── Immediate kick-out: Team_Management_Permission revoked ────────────
      // Backend returns 401 with code TEAM_PERMISSION_REVOKED when the parent
      // client's license no longer includes Team_Management_Permission.
      // Force logout immediately without attempting token refresh.
      if (status === 401 && data?.code === 'TEAM_PERMISSION_REVOKED') {
        this._forceLogout('permission_revoked');
        error.message = data.message || 'Your account access has been removed by the administrator.';
        error.code = 'TEAM_PERMISSION_REVOKED';
        return Promise.reject(error);
      }

      switch (status) {
        case HTTP_STATUS.UNAUTHORIZED:
          // Don't redirect immediately — attempt silent token refresh first.
          // If refresh succeeds, replay the original request transparently.
          // Only redirect to login if refresh itself also fails.
          return this._handleTokenRefresh(error);
        case HTTP_STATUS.FORBIDDEN:
          error.message = backendMessage || 'You are not authorized to perform this action.';
          break;
        case HTTP_STATUS.NOT_FOUND:
          error.message = backendMessage || 'The requested resource was not found.';
          break;
        case HTTP_STATUS.TOO_MANY_REQUESTS:
          error.message = backendMessage || 'Too many requests. Please try again later.';
          break;

        case HTTP_STATUS.CONFLICT: // 409
          // SESSION_LIMIT_REACHED and other conflict errors — preserve response intact
          // so authService and AuthContext can read code + sessions from error.responseData
          error.message = backendMessage || 'Conflict error.';
          break;
        case HTTP_STATUS.INTERNAL_SERVER_ERROR:
          error.message = backendMessage || ERROR_MESSAGES.SERVER_ERROR;
          break;
        case HTTP_STATUS.SERVICE_UNAVAILABLE:
          error.message = backendMessage || 'Service temporarily unavailable. Please try again later.';
          break;
        default:
          error.message = backendMessage || ERROR_MESSAGES.SERVER_ERROR;
      }

      // Attach response data for component handling
      error.responseData = data;
    } else if (request) {
      // Network error
      error.message = ERROR_MESSAGES.NETWORK_ERROR;
    } else {
      // Other error
      error.message = message || 'An unexpected error occurred.';
    }

    return Promise.reject(error);
  }

  // Silent token refresh on 401 — tries the refreshToken cookie before giving up
  async _handleTokenRefresh(originalError) {
    const originalRequest = originalError.config;
    const url = originalRequest?.url || '';

    // Never intercept these — just reject cleanly, no logout:
    // 1. Login/logout/refresh endpoints — 401 here means wrong password, not expired token
    // 2. Already-retried requests — don't loop
    const isLoginEndpoint   = url.includes('/auth/client/login') ||
                               url.includes('/auth/client-user/login') ||
                               url.includes('/auth/admin/login') ||
                               url.includes('/auth/logout');
    const isRefreshEndpoint = url.includes('/auth/refresh');
    const alreadyRetried    = originalRequest?._retry;

    if (isLoginEndpoint || alreadyRetried) {
      // Just reject — let the caller handle it (shows "Invalid credentials" etc.)
      return Promise.reject(originalError);
    }

    if (isRefreshEndpoint) {
      // Refresh itself returned 401 — session is truly dead
      this._forceLogout();
      return Promise.reject(originalError);
    }

    if (this._isRefreshing) {
      // Another request already triggered a refresh — queue this one.
      // CRITICAL: the queued Promise gets a 15s timeout so it never hangs
      // forever if the refresh call itself is stuck on a corporate proxy.
      return new Promise((resolve, reject) => {
        const queueTimeout = setTimeout(() => {
          reject(new Error('Queued request timed out waiting for token refresh'));
        }, 15000);

        this._refreshQueue.push({
          resolve: (val) => { clearTimeout(queueTimeout); resolve(val); },
          reject:  (err) => { clearTimeout(queueTimeout); reject(err); },
          config: originalRequest,
        });
      });
    }

    this._isRefreshing = true;
    this._refreshStartedAt = Date.now(); // track when refresh started
    originalRequest._retry = true;

    try {
      // Silent refresh using the refreshToken HTTP-only cookie.
      // Hard 10s timeout — on corporate networks the refresh call can hang
      // indefinitely, locking _isRefreshing=true and freezing all queued requests.
      const refreshResult = await Promise.race([
        this.api.post('/auth/refresh', {}),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Token refresh timed out')), 10000)
        ),
      ]);

      // Sync localStorage token so Bearer header stays current
      const newToken = refreshResult?.data?.data?.token;
      if (newToken) this.setAuthToken(newToken);

      this._isRefreshing = false;
      this._refreshStartedAt = null;

      // Replay all queued requests with the new token
      this._refreshQueue.forEach(({ resolve, config }) => resolve(this.api(config)));
      this._refreshQueue = [];

      // Replay the original failed request
      return this.api(originalRequest);

    } catch (refreshError) {
      // Refresh failed or timed out — force logout and unblock queue
      this._isRefreshing = false;
      this._refreshStartedAt = null;
      this._refreshQueue.forEach(({ reject }) => reject(refreshError));
      this._refreshQueue = [];
      this._forceLogout();
      return Promise.reject(originalError);
    }
  }

  _forceLogout(reason = 'session_expired') {
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    localStorage.removeItem(STORAGE_KEYS.REMEMBER_ME);
    delete this.api.defaults.headers.common['Authorization'];
    if (!window.location.pathname.includes('/login')) {
      window.location.href = `/login?reason=${reason}`;
    }
  }

  handleUnauthorized() {
    this._forceLogout();
  }

  // Generic request methods
  async get(url, params = {}, options = {}) {
    const response = await this.api.get(url, { params, ...options });
    return response.data;
  }

  async post(url, data = {}, options = {}) {
    const response = await this.api.post(url, data, options);
    return response.data;
  }

  async put(url, data = {}, options = {}) {
    const response = await this.api.put(url, data, options);
    return response.data;
  }

  async patch(url, data = {}, options = {}) {
    const response = await this.api.patch(url, data, options);
    return response.data;
  }

  async delete(url, options = {}) {
    const response = await this.api.delete(url, options);
    return response.data;
  }

  // File upload method
  async upload(url, formData, onUploadProgress = null, options = {}) {
    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
      ...options,
    };

    const response = await this.api.post(url, formData, config);
    return response.data;
  }

  // Download method
  async download(url, filename, options = {}) {
    const response = await this.api.get(url, {
      responseType: 'blob',
      ...options,
    });

    // Create download link
    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);

    return response.data;
  }

  // Set authentication token
  setAuthToken(token) {
    if (token) {
      localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
      this.api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
      delete this.api.defaults.headers.common['Authorization'];
    }
  }

  // Get authentication token
  getAuthToken() {
    return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
  }

  // Check if user is authenticated
  isAuthenticated() {
    const token = this.getAuthToken();
    const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
    return !!(token && userData);
  }

  // Get current user data
  getCurrentUser() {
    const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
    return userData ? JSON.parse(userData) : null;
  }

  // Set current user data
  setCurrentUser(userData) {
    if (userData) {
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
    } else {
      localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    }
  }

  // Clear all authentication data
  clearAuth() {
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    localStorage.removeItem(STORAGE_KEYS.REMEMBER_ME);
    delete this.api.defaults.headers.common['Authorization'];
  }

  // Health check
  async healthCheck() {
    try {
      const response = await this.get('/system/health');
      return response;
    } catch (error) {
      //console.warn('Health check failed:', error.message);
      return { status: 'error', message: error.message };
    }
  }

  // ===== AUTHENTICATION METHODS =====

  // Client login
  // ── LOGIN CALLS: use a fresh axios instance with NO interceptors
  //    This guarantees the 409 SESSION_LIMIT_REACHED response is never
  //    swallowed, mutated, or redirected by the response interceptor.
  _buildLoginClient() {
    return axios.create({
      baseURL:      this.api.defaults.baseURL,
      timeout:      this.api.defaults.timeout,
      withCredentials: true,
      headers:      { 'Content-Type': 'application/json' },
    });
  }

  async clientLogin(email, password) {
    const loginAxios = this._buildLoginClient();
    try {
      const res = await loginAxios.post('/auth/client/login', { email, password });
      // 2xx success
      const data = res.data;
      //console.log('[api.clientLogin] success:', data?.success, 'token:', !!data?.data?.token);
      if (data?.success && data?.data?.token) {
        this.setAuthToken(data.data.token);
        this.setCurrentUser(data.data.user);
      }
      return data;
    } catch (axiosError) {
      const status = axiosError?.response?.status;
      const data   = axiosError?.response?.data;
      //console.log('[api.clientLogin] error status:', status, 'code:', data?.code, 'data:', data);

      if (status === 409 && data?.code === 'SESSION_LIMIT_REACHED') {
        const err        = new Error(data.message || 'Session limit reached');
        err.code         = 'SESSION_LIMIT_REACHED';
        err.status       = 409;
        err.details      = data;
        err.responseData = data;
        throw err;
      }
      // Any other error — clean message
      const msg = data?.message || data?.error || axiosError.message || 'Login failed';
      const cleanErr = new Error(msg);
      cleanErr.status       = status;
      cleanErr.responseData = data;
      throw cleanErr;
    }
  }

  async clientUserLogin(email, password) {
    const loginAxios = this._buildLoginClient();
    try {
      const res = await loginAxios.post('/auth/client-user/login', { email, password });
      const data = res.data;
      //console.log('[api.clientUserLogin] success:', data?.success);
      if (data?.success && data?.data?.token) {
        this.setAuthToken(data.data.token);
        const userData = {
          ...data.data.user,
          parentClient: data.data.parentClient || null,
          license:      data.data.license      || null,
          branding: data.data.parentClient ? {
            company_name:  data.data.parentClient.company_name,
            primary_color: data.data.parentClient.primary_color,
            accent_color:  data.data.parentClient.accent_color,
          } : null,
        };
        this.setCurrentUser(userData);
      }
      return data;
    } catch (axiosError) {
      const status = axiosError?.response?.status;
      const data   = axiosError?.response?.data;
      //console.log('[api.clientUserLogin] error status:', status, 'code:', data?.code);

      if (status === 409 && data?.code === 'SESSION_LIMIT_REACHED') {
        const err        = new Error(data.message || 'Session limit reached');
        err.code         = 'SESSION_LIMIT_REACHED';
        err.status       = 409;
        err.details      = data;
        err.responseData = data;
        throw err;
      }
      const msg = data?.message || data?.error || axiosError.message || 'Login failed';
      const cleanErr = new Error(msg);
      cleanErr.status       = status;
      cleanErr.responseData = data;
      throw cleanErr;
    }
  }

  // Verify token
  async verifyToken() {
    return await this.get('/auth/verify');
  }

  // Refresh token
  async refreshToken(refreshToken) {
    return await this.post('/auth/refresh', { refreshToken });
  }

  // Logout
  async logout() {
    const response = await this.post('/auth/logout');
    this.clearAuth();
    return response;
  }
// Add this to your apiService
getProxiedImageUrl(originalUrl) {
  if (!originalUrl) return null;
  return `/api/image-proxy?url=${encodeURIComponent(originalUrl)}`;
}
  // ===== CLIENT PROFILE & BRANDING METHODS =====

  // Get client profile
  // async getClientProfile() {
  //   const userType = this.getCurrentUser()?.userType;
  //   const endpoint = userType === 'client_child_user' ? '/client-user/profile' : '/client/profile';
  //   return await this.get(endpoint);
  // }

  // Update profile with JSON
  async updateClientProfile(profileData,userType) {
    // console.log("userType----",userType)
    const endpoint = userType === 'client_child_user' ? '/client-user/profile' : '/client/clientProfile';
    return await this.put(endpoint, profileData);
  }

  // Update profile with file uploads
async updateClientProfileWithFiles(formData, userType) {
  //console.log("userType----", userType);
  const endpoint = userType === 'client_child_user' 
    ? '/client-user/profile' 
    : '/client/clientProfile';

  // ✅ Use PUT explicitly, not upload() which uses POST
  
  const response = await this.api.put(endpoint, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
}
getProfileImageUrl(filePath) {
  if (!filePath) return null;
  const filename = filePath.split(/[/\\]/).pop();
  return `${config.apiBaseURL}/files/client/profiles/${filename}`;
}

getCompanyLogoUrl(filePath) {
  if (!filePath) return null;
  const filename = filePath.split(/[/\\]/).pop();
  return `${config.apiBaseURL}/files/client/logos/${filename}`;
}


  // Get client license
  async getClientLicense() {
    return await this.get('/client/license');
  }
  async getClientChildDetails() {
    return await this.get('/client_child_user/details');
  }

  // Get constant by key
  async getConstantByKey(key) {
    const userType = this.getCurrentUser()?.userType;
    const baseRoute = userType === 'client_child_user' ? '/client-user' : '/client';
    return await this.get(`${baseRoute}/constants/key/${key}`);
  }

  // Change password
  async changePassword(oldPassword, newPassword) {
    return await this.put('/client/password', {
      oldPassword,
      newPassword
    });
  }

  // ===== FILE MANAGEMENT METHODS =====

  // Get profile image
  // getProfileImageUrl(filename) {
  //   return `${config.apiBaseURL}/files/client/profiles/${filename}`;
  // }

  // Get company logo
  // getCompanyLogoUrl(filename) {
  //   return `${config.apiBaseURL}/files/client/logos/${filename}`;
  // }

  // Get file info
  async getFileInfo(path, filename) {
    return await this.get(`/files/client/${path}/${filename}/info`);
  }

  // ===== CLIENT USER MANAGEMENT METHODS =====

  // Get client users
  async getClientUsers(page = 1, limit = 20, status = null, search = null) {
    const params = { page, limit };
    if (status !== null) params.status = status;
    if (search) params.search = search;

    return await this.get('/client/users', params);
  }

  // Create client user
  async createClientUser(userData) {
    return await this.post('/client/users', userData);
  }

  // Update client user
  async updateClientUser(userId, userData) {
    return await this.put(`/client/users/${userId}`, userData);
  }

  // Delete client user
  async deleteClientUser(userId) {
    return await this.delete(`/client/users/${userId}`);
  }

  // ===== CLIENT ADMIN PANEL METHODS =====

  async getClientAdminStats() {
    return await this.get('/client-admin/stats');
  }

  // Roles
  async getClientRoles(search = '') {
    const params = search ? `?search=${encodeURIComponent(search)}` : '';
    return await this.get(`/client-admin/roles${params}`);
  }

  async createClientRole(roleData) {
    return await this.post('/client-admin/roles', roleData);
  }

  async updateClientRole(roleId, roleData) {
    return await this.put(`/client-admin/roles/${roleId}`, roleData);
  }

  async deleteClientRole(roleId) {
    return await this.delete(`/client-admin/roles/${roleId}`);
  }

  async seedDefaultRoles() {
    return await this.post('/client-admin/roles/seed-defaults', {});
  }

  // Admin users list (with role info)
  async getClientAdminUsers(params = {}) {
    const qs = new URLSearchParams();
    if (params.page)   qs.set('page', params.page);
    if (params.limit)  qs.set('limit', params.limit);
    if (params.status !== undefined && params.status !== null) qs.set('status', params.status);
    if (params.search) qs.set('search', params.search);
    const query = qs.toString();
    return await this.get(`/client-admin/users${query ? '?' + query : ''}`);
  }

  async assignRoleToUser(userId, roleId) {
    return await this.put(`/client-admin/users/${userId}/assign-role`, { role_id: roleId });
  }

  // ===== TRADEMARK ACCESS METHODS =====

  // Full portfolio list for assignment UI (client-admin only)
  async getPortfolioForAssignment(params = {}) {
    const qs = new URLSearchParams();
    if (params.userId) qs.set('userId', params.userId);
    if (params.search) qs.set('search', params.search);
    if (params.type)   qs.set('type', params.type);
    const q = qs.toString();
    return await this.get(`/client-admin/portfolio${q ? '?' + q : ''}`);
  }

  // Get assigned trademarks for a specific user
  async getUserTrademarks(userId) {
    return await this.get(`/client-admin/users/${userId}/trademarks`);
  }

  // Full sync: replace all assigned trademarks for a user
  async syncUserTrademarks(userId, tmIds) {
    return await this.post(`/client-admin/users/${userId}/trademarks/sync`, { tm_ids: tmIds });
  }

  // Set per-user override: null | 1 | 0
  async setTrademarkOverride(userId, override) {
    return await this.put(`/client-admin/users/${userId}/trademark-override`, { override });
  }

  // ===== TEAM MANAGEMENT METHODS =====

  async getTeams() {
    return await this.get('/client-admin/teams');
  }

  async createTeam(data) {
    return await this.post('/client-admin/teams', data);
  }

  async updateTeam(teamId, data) {
    return await this.put(`/client-admin/teams/${teamId}`, data);
  }

  async deleteTeam(teamId) {
    return await this.delete(`/client-admin/teams/${teamId}`);
  }

  async setTeamMembers(teamId, memberIds) {
    return await this.put(`/client-admin/teams/${teamId}/members`, { member_ids: memberIds });
  }

  // ===== HIERARCHY (ORG CHART) METHODS =====

  async getHierarchyTree() {
    return await this.get('/client-admin/hierarchy');
  }

  async setUserReportsTo(userId, managerId, hierarchyLevel = null, assignToOwner = false) {
    return await this.put(`/client-admin/users/${userId}/reports-to`, {
      manager_id: managerId,
      hierarchy_level: hierarchyLevel,
      assign_to_owner: assignToOwner,
    });
  }

  async promoteDirectReports(userId, newManagerId) {
    return await this.post(`/client-admin/users/${userId}/promote-reports`, {
      new_manager_id: newManagerId,
    });
  }

  // ===== LEGAL DRAFTING MODULE =====

  async getLegalDraftingTaxonomy() {
    return await this.get('/legal-drafting/taxonomy');
  }

  async getLegalDraftingObjectionDetails(params = {}) {
    const qs = new URLSearchParams();
    if (params.application_number) qs.set('application_number', params.application_number);
    if (params.deadline_id)        qs.set('deadline_id', params.deadline_id);
    if (params.drafting_type)      qs.set('drafting_type', params.drafting_type);
    const q = qs.toString();
    return await this.get(`/legal-drafting/objection-details${q ? '?' + q : ''}`);
  }

  async listLegalDraftingCases(params = {}) {
    const qs = new URLSearchParams();
    if (params.branch)        qs.set('branch', params.branch);
    if (params.matter_type)   qs.set('matter_type', params.matter_type);
    if (params.drafting_type) qs.set('drafting_type', params.drafting_type);
    if (params.search)        qs.set('search', params.search);
    if (params.source)        qs.set('source', params.source);
    if (params.page)          qs.set('page', params.page);
    if (params.limit)         qs.set('limit', params.limit);
    const q = qs.toString();
    return await this.get(`/legal-drafting/cases${q ? '?' + q : ''}`);
  }

  async createLegalDraftingCase(data) {
    return await this.post('/legal-drafting/cases', data);
  }

  async updateLegalDraftingCase(id, data) {
    return await this.put(`/legal-drafting/cases/${id}`, data);
  }

  async deleteLegalDraftingCase(id) {
    return await this.delete(`/legal-drafting/cases/${id}`);
  }

  async listSampleReplies(params = {}) {
    const qs = new URLSearchParams();
    if (params.drafting_type)      qs.set('drafting_type', params.drafting_type);
    if (params.application_number) qs.set('application_number', params.application_number);
    if (params.matter_reference)   qs.set('matter_reference', params.matter_reference);
    const q = qs.toString();
    return await this.get(`/legal-drafting/sample-replies${q ? '?' + q : ''}`);
  }

  async uploadSampleReply(formData, onProgress = null) {
    return await this.upload('/legal-drafting/sample-replies', formData, onProgress);
  }

  async uploadExamReport(formData, onProgress = null) {
    return await this.upload('/legal-drafting/exam-reports', formData, onProgress);
  }

  async deleteSampleReply(id) {
    return await this.delete(`/legal-drafting/sample-replies/${id}`);
  }

  async getLegalDraftingConfig(params = {}) {
    const qs = new URLSearchParams();
    if (params.drafting_type)  qs.set('drafting_type', params.drafting_type);
    if (params.objection_type) qs.set('objection_type', params.objection_type);
    const q = qs.toString();
    return await this.get(`/legal-drafting/config${q ? '?' + q : ''}`);
  }

  async saveLegalDraftingConfig(data) {
    return await this.post('/legal-drafting/config', data);
  }

  async listLegalDrafts(params = {}) {
    const qs = new URLSearchParams();
    if (params.drafting_type)      qs.set('drafting_type', params.drafting_type);
    if (params.application_number) qs.set('application_number', params.application_number);
    if (params.matter_reference)   qs.set('matter_reference', params.matter_reference);
    const q = qs.toString();
    return await this.get(`/legal-drafting/drafts${q ? '?' + q : ''}`);
  }

  async generateLegalDraft(payload) {
    return await this.post('/legal-drafting/drafts/generate', payload);
  }

  async getLegalDraft(id) {
    return await this.get(`/legal-drafting/drafts/${id}`);
  }

  async cancelLegalDraft(id) {
    return await this.post(`/legal-drafting/drafts/${id}/cancel`);
  }

  // ── Legal Drafting — Prompt blocks (admin + custom mode section picker) ──
  async listLegalDraftingPromptBlocks() {
    return await this.get('/legal-drafting/admin/prompts');
  }
  async getLegalDraftingPromptBlock(key) {
    return await this.get(`/legal-drafting/admin/prompts/${key}`);
  }
  async updateLegalDraftingPromptBlock(key, data) {
    return await this.put(`/legal-drafting/admin/prompts/${key}`, data);
  }
  async reloadLegalDraftingPromptCache() {
    return await this.post('/legal-drafting/admin/prompts/reload', {});
  }

  // Upload opposition notice PDF (for counter statement)
  async uploadOppositionNotice(formData, onProgress = null) {
    return await this.upload('/legal-drafting/opposition-notices', formData, onProgress);
  }

  // Upload Rule 45 affidavit PDF (for Rule 46 drafting context)
  async uploadRule45Affidavit(formData, onProgress = null) {
    return await this.upload('/legal-drafting/rule45-affidavits', formData, onProgress);
  }

  // Upload Rule 46 affidavit PDF (for Rule 47 drafting context)
  async uploadRule46Affidavit(formData, onProgress = null) {
    return await this.upload('/legal-drafting/rule46-affidavits', formData, onProgress);
  }
// ── Legal Drafting — Download generated .docx ─────────────────────────────
async downloadLegalDraft(draft) {
  const DRAFTING_LABELS = {
    tm_examination_reply:     'Reply_to_examination_report',
    tm_opposition_filing:     'Opposition_filing',
    tm_counter_statement:     'Counter_statement_reply',
    tm_rule_45:               'Rule_45',
    tm_rule_46:               'Rule_46',
    tm_rule_47:               'Rule_47',
    tm_show_cause:            'Show_cause_hearing_document',
    tm_rectification:         'Rectification_petition',
    design_examination_reply: 'Reply_to_examination_report_design',
    design_opposition:        'Opposition_to_design_registration',
    patent_fer_reply:         'Reply_to_first_examination_report',
    cr_cease_desist:          'Cease_and_desist_letter',
    civil_plaint:             'Plaint',
    civil_written_statement:  'Written_statement',
    civil_injunction:         'Interim_injunction_application',
    civil_legal_notice:       'Legal_notice',
    crim_bail:                'Bail_application',
    consumer_complaint:       'Consumer_complaint',
    rera_complaint:           'RERA_complaint',
  };

  const labelFor = (key) =>
    DRAFTING_LABELS[key] || (key ? key.replace(/_/g, ' ') : 'Document');

  const docLabel = labelFor(draft.drafting_type);
  const filename = `${docLabel}_v${draft.version || 1}.docx`;

  // Uses the same axios instance — baseURL, Bearer token, withCredentials all applied
  const response = await this.api.get(
    `/legal-drafting/drafts/${draft.id}/download`,
    {
      responseType: 'arraybuffer',
      timeout: parseInt(process.env.REACT_APP_API_TIMEOUT, 10) || 180000,
    }
  );

  const data = response.data; // ArrayBuffer

  // Validate DOCX magic bytes: ZIP PK = 0x50 0x4B
  const uint8 = new Uint8Array(data);
  if (uint8[0] !== 0x50 || uint8[1] !== 0x4B) {
    const preview = new TextDecoder('utf-8', { fatal: false }).decode(data.slice(0, 300));
    try {
      const parsed = JSON.parse(preview);
      throw new Error(parsed.message || 'Server returned an error instead of the file');
    } catch (e) {
      if (e.message && !e.message.includes('JSON')) throw e;
      throw new Error('Downloaded file is invalid. Please regenerate the draft.');
    }
  }

  // Build Blob and trigger browser download
  const blob = new Blob([data], {
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });
  const url = window.URL.createObjectURL(blob);
  const a   = document.createElement('a');
  a.href          = url;
  a.download      = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }, 200);
}
  // ===== TASK MANAGEMENT METHODS =====

  async getMyTeamPermissions() {
    return await this.get('/tasks/my-permissions');
  }

  async getTaskStats() {
    return await this.get('/tasks/stats');
  }

  async getTeamMembers() {
    return await this.get('/tasks/members');
  }

  async getTasks(params = {}) {
    const qs = new URLSearchParams();
    if (params.status)      qs.set('status', params.status);
    if (params.priority)    qs.set('priority', params.priority);
    if (params.assigned_to) qs.set('assigned_to', params.assigned_to);
    if (params.search)      qs.set('search', params.search);
    if (params.page)        qs.set('page', params.page);
    if (params.limit)       qs.set('limit', params.limit);
    const q = qs.toString();
    return await this.get(`/tasks${q ? '?' + q : ''}`);
  }

  async createTask(taskData) {
    return await this.post('/tasks', taskData);
  }

  async updateTask(taskId, taskData) {
    return await this.put(`/tasks/${taskId}`, taskData);
  }

  async deleteTask(taskId) {
    return await this.delete(`/tasks/${taskId}`);
  }

  async getDeadlineReminders(params = {}) {
    const qs = new URLSearchParams();
    if (params.application_number) qs.set('application_number', params.application_number);
    if (params.status)             qs.set('status', params.status);
    const q = qs.toString();
    return await this.get(`/tasks/reminders/list${q ? '?' + q : ''}`);
  }

  async createDeadlineReminder(data) {
    return await this.post('/tasks/reminders', data);
  }

  async updateDeadlineReminder(id, data) {
    return await this.put(`/tasks/reminders/${id}`, data);
  }

  async deleteDeadlineReminder(id) {
  return await this.delete(`/tasks/reminders/${id}`);
}

  
  // ===== TASK COMMENTS =====
  async getTaskComments(taskId) {
    return await this.get(`/tasks/${taskId}/comments`);
  }
  async addTaskComment(taskId, comment) {
    return await this.post(`/tasks/${taskId}/comments`, { comment });
  }
  async editTaskComment(taskId, commentId, comment) {
    return await this.put(`/tasks/${taskId}/comments/${commentId}`, { comment });
  }
  async deleteTaskComment(taskId, commentId) {
    return await this.delete(`/tasks/${taskId}/comments/${commentId}`);
  }

  // ===== TRADEMARK SEARCH METHODS =====

  // Main trademark search - uses the /search/simple endpoint as per API documentation
async searchTrademarks(searchData) {
  return await this.post('/trademark-search/name', searchData);
}

async fetchTrademarkDetails(tmNumber, is_SearchType, reportMode = false) {
  return await this.post('/trademark-search/number', {
    ref_id: parseInt(tmNumber, 10),
    ctx_flag: Boolean(is_SearchType),
    export_mode: Boolean(reportMode)
  });
}

async fetchMultipleTrademarkDetails(tmNumbers, is_SearchType, reportMode = false) {
  return await this.post('/trademark-search/number', {
    ref_ids: tmNumbers.map(num => parseInt(num, 10)),
    ctx_flag: Boolean(is_SearchType),
    export_mode: Boolean(reportMode)
  });
}
// ─── Design Search ─────────────────────────────────────────────────────────────
async designSearch(searchData) {
  return await this.post('/design-search/search', searchData);
}
// ─── Patent Search ─────────────────────────────────────────────────────────────

async patentSearch(searchData) {
  return await this.post('/patent-search/search', searchData);
}
async searchInterNationalTrademarks(searchData) {
  // console.log("search international data-------",searchData)
  return await this.post('/international-search/search', searchData);
}

async searchWatchTrademarks(searchData) {
  // console.log("searchTrademarks HIT", searchData);
  return await this.post('/trademark/search/simple-watch', searchData);
}


// Free search — uses raw axios (no auth interceptor) — public page, no login session
async freeSearchTrademarks(searchData) {
  const response = await axios.post(
    `${this.api.defaults.baseURL}/free-search/simple`,
    searchData,
    { withCredentials: false }
  );
  return response.data;
}

// Exchange CAPTCHA answer for a single-use server-side token
// Uses raw axios (no auth interceptor) — free page has no login session
async issueCaptchaToken({ captchaCode, captchaAnswer }) {
  const response = await axios.post(
    `${this.api.defaults.baseURL}/free-search/token`,
    { captchaCode, captchaAnswer },
    { withCredentials: false }
  );
  return response.data;
}

async fetchProprietorTrademarks(payload, reportMode) {
  return await this.post('/trademark/fetch/proprietor-trademarks', payload,reportMode);
}


  // Refresh  detailed trademark information
// apiService.js
async refreshTrademarkDetails(payload) {
  return await this.post("/trademark/fetch/request_tm_update", payload);
}
async autoRefreshTrademarkOnOpen(payload) {
  return await this.post("/trademark/fetch/auto_tm_update", payload);
}
async refreshMultipleTrademarkDetails(payload) {
  return await this.post("/trademark/fetch/refreshMultipleTrademarkDetails", payload);
}



  // Fetch proprietor details
  async fetchProprietorDetails(proprietorId,val) {
    return await this.post('/trademark/fetch/proprietor', {
      proprietor_id: parseInt(proprietorId)
    });
  }

  // Fetch attorney details
  async fetchAttorneyDetails(attorneyCode) {
    return await this.post('/trademark/fetch/attorney', {
      attorney_code: parseInt(attorneyCode)
    });
  }

  // Fetch opposition details
  async fetchOppositionTracker(application_number) {
    return await this.post('/trademark/fetch/opposition-tracker', { application_number: parseInt(application_number) });
  }
  // Add this inside your ApiService class
async fetchTMManagerOwn(searchData) {
  try {

    const response = await this.api.post('trademark/fetch/tm-managerown', searchData);
      // console.log('Error fetching TM Manager:Your', response.data.toString() );

      return response.data;
  } catch (error) {
    //console.error('Error fetching TM Manager:', error);
    throw error;
  }
}

async fetchTMManagerOpponent(searchData) {
  try {

    const response = await this.api.post('trademark/fetch/tm-managerOpponent', searchData);
      // console.log('TM Manager:', "Opponent",searchData);
      return response.data;
  } catch (error) {
    //console.error('Error fetching TM Manager:', error);
    throw error;
  }
}
async fetchTMManagerSingle(application_number) {
  const response = await this.api.post(
    "trademark/fetch/tm-managerSingle", // 🔥 EXACT match
    { application_number }
  );
  return response.data;
}


async fetchTMManagerAll(searchData) {
  try {
  //  console.log('Error fetching TM Manager:', "All");

    const response = await this.api.post('trademark/fetch/tm-managerAll', searchData);
    return response.data;
  } catch (error) {
    //console.error('Error fetching TM Manager:', error);
    throw error;
  }
}
async fetchTMManagerTrademarks(type,type_1) {
  // console.log("type is ===",type_1)
  try {
    const response = await this.api.post('trademark/fetch/tm-manager/trademarks', {type,type_1});
    // const response = await this.api.post('trademark/fetch/trademark', {type});
    return response.data;
  } catch (error) {
    //console.error('Error fetching TM Manager:', error);
    throw error;
  }
}

// ===== STATUS CHANGE TRACKER =====
// Fetch status change history for given application numbers
// Endpoint: POST /watch/fetch-change-status
async fetchChangeStatus(application_numbers = []) {
  try {
    const response = await this.api.post('/watch/fetch-change-status', {
      application_numbers,
    });
    return response.data;
  } catch (error) {
    //console.error('Error fetching change status:', error);
    throw error;
  }
}

// ===== ATTORNEY CHANGE TRACKER =====
// Fetch attorney change history for given application numbers
// Endpoint: POST /watch/fetch-attorney-change-status
// Body: { application_numbers: [...] }
// Response: { success, data: { changes_by_application: [{ application_number, changes: [...] }] } }
async fetchAttorneyChangeStatus(application_numbers = []) {
  try {
    const response = await this.api.post('/watch/fetch-attorney-change-status', {
      application_numbers,
    });
    return response.data;
  } catch (error) {
    //console.error('Error fetching attorney change status:', error);
    throw error;
  }
}
 

  // Get search history
  async getSearchHistory(page = 1, limit = 20, searchType = null, fromDate = null, toDate = null) {
    const params = { page, limit };
    if (searchType) params.search_type = searchType;
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;

    return await this.get('/trademark/history', params);
  }

  // Get quota information
  async getQuotaInfo() {
    return await this.get('/trademark/quota');
  }

  // Get data models
  async getDataModels() {
    return await this.get('/trademark/data-models');
  }

  // Get specific data model
  async getDataModel(modelName) {
    return await this.get(`/trademark/data-models/${modelName}`);
  }

  // Check trademark API health
  async checkTrademarkApiHealth() {
    return await this.get('/trademark/health');
  }

  // Fetch document by ID and category/type
  // async fetchDocument(documentId, documentCategory, documentType) {
  //   try {
  //     const response = await this.post('/trademark/fetch/document', {
  //       id: parseInt(documentId),
  //       document_category: documentCategory,
  //       document_type: documentType
  //     });

  //     // If response contains b2_link, it's a download link
  //     if (response.data && response.data.b2_link) {
  //       return {
  //         success: true,
  //         type: 'link',
  //         url: response.data.b2_link,
  //         data: response.data
  //       };
  //     }

  //     // Otherwise it's a direct download (blob/stream)
  //     return {
  //       success: true,
  //       type: 'direct',
  //       data: response.data
  //     };
  //   } catch (error) {
  //     console.error('Error fetching document:', error);
  //     throw error;
  //   }
  // }

  // new  fetch  version 
async fetchDocument(documentId, documentCategory, documentType, fileNameBase = null) {
  try {
    const response = await this.api.post(
      '/trademark/fetch/document',
      {
        id: parseInt(documentId, 10),
        document_category: documentCategory,
        document_type: documentType,
      },
      {
        responseType: 'blob', // JSON or PDF stream for both
      }
    );

    const contentType = response.headers['content-type'] || '';

    // 🔹 CASE 1: JSON (b2_link) → document_type = "document" wala case
    if (contentType.includes('application/json')) {
      const text = await response.data.text();      // blob → text
      const json = JSON.parse(text);

      const link =
        json.data?.b2_link ||
        json.b2_link ||
        json.data?.url ||
        json.url;

      if (!link) {
        return {
          success: false,
          message: 'Document link not found in response',
          data: json,
        };
      }

      // File name ready
      let extension = '';
      try {
        const urlObj = new URL(link);
        const pathname = urlObj.pathname;
        const lastDot = pathname.lastIndexOf('.');
        if (lastDot !== -1) {
          extension = pathname.substring(lastDot); // ".pdf", ".jpg", ...
        }
      } catch (e) {}

      const base =
        fileNameBase
          ? fileNameBase.replace(/[^a-zA-Z0-9_\-\s]/g, '_')
          : `document_${documentId}`;

      const finalName = extension ? `${base}${extension}` : base;

      // Download via <a>
      const a = document.createElement('a');
      a.href = link;
      a.target = '_blank';
      a.download = finalName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      return {
        success: true,
        type: 'link',
        url: link,
        data: json,
      };
    }

    // 🔹 CASE 2: direct PDF / image stream (correspondence wala case)
    const blob = new Blob([response.data], {
      type: contentType || 'application/pdf',
    });

    const url = window.URL.createObjectURL(blob);

    const base =
      fileNameBase
        ? fileNameBase.replace(/[^a-zA-Z0-9_\-\s]/g, '_')
        : `document_${documentId}`;

    // Extension guess (optional)
    let ext = '';
    if (contentType.includes('pdf')) ext = '.pdf';
    else if (contentType.includes('png')) ext = '.png';
    else if (contentType.includes('jpeg') || contentType.includes('jpg')) ext = '.jpg';

    const finalName = `${base}${ext}`;

    const a = document.createElement('a');
    a.href = url;
    a.download = finalName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);

    return {
      success: true,
      type: 'direct',
    };
  } catch (error) {
    //console.error('Error fetching/downloading document:', error);
    throw error;
  }
}


// ApiService class ke andar
async getDocumentBlobForView(documentId, documentCategory, documentType) {
  try {
    const response = await this.api.post(
      '/trademark/fetch/document',
      {
        id: parseInt(documentId, 10),
        document_category: documentCategory,
        document_type: documentType,
      },
      {
        responseType: 'blob',  // 👈 IMPORTANT
      }
    );

    // // response.data = Blob
    // console.log('getDocumentBlobForView OK:', {
    //   blobType: response.data?.type,
    //   size: response.data?.size,
    // });

    return {
      success: true,
      blob: response.data,
    };
  } catch (error) {
    //console.error('Error in getDocumentBlobForView:', error);
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.responseData?.message ||
        error.message ||
        'Failed to fetch document',
    };
  }
}



  // Download document directly (handles both link and stream responses)
  async downloadDocument(documentId, documentCategory, documentType, fileName = null) {
    try {
      const result = await this.fetchDocument(documentId, documentCategory, documentType);

      if (result.type === 'link' && result.url) {
        // Open the B2 link in a new window for download
        const link = document.createElement('a');
        link.href = result.url;
        link.target = '_blank';
        link.download = fileName || `document_${documentId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return { success: true, message: 'Document download started' };
      } else if (result.type === 'direct') {
        // Handle direct stream/blob download
        const blob = new Blob([result.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName || `document_${documentId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        return { success: true, message: 'Document downloaded' };
      }

      return { success: false, message: 'Unknown response type' };
    } catch (error) {
      //console.error('Error downloading document:', error);
      throw error;
    }
  }

  // ===== SEARCH MANAGEMENT METHODS =====

  // Get search history (detailed)
  async getDetailedSearchHistory(page = 1, limit = 20, searchType = null, fromDate = null, toDate = null) {
    const params = { page, limit };
    if (searchType) params.search_type = searchType;
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;

    return await this.get('/search/history', params);
  }

  // Get search history by ID
  async getSearchHistoryById(id) {
    return await this.get(`/search/history/${id}`);
  }

  // Record search
  async recordSearch(searchData) {
    return await this.post('/search/history', searchData);
  }

  // Get search statistics
  async getSearchStatistics(fromDate = null, toDate = null) {
    const params = {};
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    //console.log("params data0----",params)
    return await this.get('/search/statistics', params);
  }

  // Get top searches
  async getTopSearches(limit = 10) {
    return await this.get('/search/top-searches', { limit });
  }

  // Get quota usage
  async getQuotaUsage(page = 1, limit = 31, fromDate = null, toDate = null) {
    const params = { page, limit };
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;

    return await this.get('/search/quota/usage', params);
  }

  // Get current quota status
  async getCurrentQuotaStatus() {
    return await this.get('/search/quota/status');
  }

  // ===== USER MANAGEMENT METHODS =====

  // Get user preferences
  async getUserPreferences() {
    return await this.get('/user/preferences');
  }

  // Update user preferences
  async updateUserPreferences(preferences) {
          //console.log("Received settings:", preferences);

    return await this.put('/user/preferences', preferences);
  }

  // Get user settings (alias for preferences for backward compatibility)
  async getUserSettings() {
    return await this.getUserPreferences();
  }

  // Update user settings (alias for preferences)
  async updateUserSettings(settings) {
      //console.log("Received settings:", settings);

    return await this.updateUserPreferences(settings);
  }

  // Get user notes
  async getUserNotes(page = 1, limit = 20) {
    return await this.get('/user/notes', { page, limit });
  }

  // Get note by ID
  async getNoteById(noteId) {
    return await this.get(`/user/notes/${noteId}`);
  }

  // Create note
  async createNote(noteData) {
    return await this.post('/user/notes', noteData);
  }

  // Update note
  async updateNote(noteId, noteData) {
    return await this.put(`/user/notes/${noteId}`, noteData);
  }

  // Delete note
  async deleteNote(noteId) {
    return await this.delete(`/user/notes/${noteId}`);
  }

  // Get user reports
  async getUserReports(page = 1, limit = 20) {
    return await this.get('/user/reports', { page, limit });
  }

  // Get report by ID
  async getReportById(reportId) {
    return await this.get(`/user/reports/${reportId}`);
  }

  // Create report
  async createReport(reportData) {
    return await this.post('/user/reports', reportData);
  }

  // Delete report
  async deleteReport(reportId) {
    return await this.delete(`/user/reports/${reportId}`);
  }

  // Get user documents
  async getUserDocuments(page = 1, limit = 20) {
    return await this.get('/user/documents', { page, limit });
  }

  // Get documents by trademark
  async getDocumentsByTrademark(trademarkNumber) {
    return await this.get(`/user/documents/trademark/${trademarkNumber}`);
  }

  // Create document
  async createDocument(documentData) {
    return await this.post('/user/documents', documentData);
  }

  // Delete document
  async deleteDocument(documentId) {
    return await this.delete(`/user/documents/${documentId}`);
  }

  // Get user dashboard data
  async getUserDashboard() {
    return await this.get('/user/dashboard');
  }

  // ===== SYSTEM METHODS =====

  // System health check
  async systemHealthCheck() {
    return await this.get('/system/health');
  }

  // ===== ADDITIONAL METHODS FOR CLIENT PANEL =====

  // Upload document
  async uploadDocument(formData) {
    return await this.upload('/user/documents', formData);
  }

  // Save/bookmark search
  async saveSearchHistory(searchId) {
    return await this.post(`/search/history/${searchId}/save`);
  }

  // Delete from search history
  async deleteSearchHistory(searchId) {
    return await this.delete(`/search/history/${searchId}`);
  }

  // Get billing data (for client billing dashboard)
  async getBillingData() {
    return await this.get('/client/license');
  }

  // Get license data (more detailed than basic license)
  async getLicenseData() {
    return await this.getClientLicense();
  }
    // Trademark Notes
    async fetchTMNotes(application_number) {
        return this.post('/trademark/notes/fetch', { application_number });
    }

    async addTMNote(data) {
      // console.log(data)
        return this.post('/trademark/notes/add', data);
    }

    async updateTMNote(data) {
        return this.post(`/trademark/notes/update/${data.id}`, data);
    }

    async deleteTMNote(id) {
        return this.post(`/trademark/notes/delete/${id}`);
    }


  //examination report
async examinationReport(tmNumbers) {
  return this.post('/watch/examination_report', {
    tm_numbers: tmNumbers
  });
}

//application status 

// apiService.js
// ✅ CORRECT
async getTrademarkStatusList() {
  return await this.get('/trademark/status-list');
}

// ================= APPLICATION STATUS TRACK =================

// Add  application status
async addApplicationStatus(data) {

  return await this.post("/trademark/application-status/add", data);
}
// Add application status (BULK — new)
async addApplicationStatusBulk(data) {
  return await this.post("/trademark/application-status/bulk-add", data);
}
async updateApplicationStatusBulk(data) {
  return await this.put("/trademark/application-status/bulk-update", data);
}
// ===== APPLICATION STATUS + TM MANAGER =====
async getApplicationStatusTrademarks() {
  return await this.post(
    "trademark/application-status/fetch"
  );
}

// ===== APPLICATION STATUS Delete =====
async deleteApplicationStatus(Data) {
  return await this.post(
    "trademark/application-status/delete",
    Data
  );
}
// ===== APPLICATION STATUS update =====
async updateApplicationStatus(Data) {
  return await this.put(
    "trademark/application-status/update",
    Data
  );
}


// uploadSearchReport
async uploadSearchReport(formData) {
  return await this.upload("/client/reports/upload", formData);
}

// get search reports
getClientReports() {
  return this.get("/client/reports/list");
}


// 🔍 View search report (PDF iframe)
// 👁️ View search report (iframe)
// 👁️ View search report (axios based)
async viewClientReport(reportId) {
  const response = await this.api.get(
    `/client/reports/view/${reportId}`,
    {
      responseType: "blob",
    }
  );

  // ✅ SAFE fallback (important)
  const contentType =
    response.headers?.["content-type"] ||
    response.data?.type ||
    "application/pdf";

  const blob = new Blob([response.data], {
    type: contentType,
  });

  const url = window.URL.createObjectURL(blob);

  return {
    url,
    contentType,
  };
}


// ⬇️ Download search report
async downloadClientReport(reportId, fileName = "search_report") {
  const response = await this.api.get(
    `/client/reports/download/${reportId}`,
    {
      responseType: "blob",
    }
  );

  // ✅ get real content type from backend
  const contentType = response.headers["content-type"];

  const blob = new Blob([response.data], {
    type: contentType,
  });

  const url = window.URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;

  // ✅ set correct extension automatically
  let finalName = fileName;

  if (contentType.includes("spreadsheet")) {
    finalName = fileName.endsWith(".xlsx") ? fileName : `${fileName}.xlsx`;
  } else {
    finalName = fileName.endsWith(".pdf") ? fileName : `${fileName}.pdf`;
  }

  a.download = finalName;

  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);

  window.URL.revokeObjectURL(url);

  return response.data;
}

// apiService
async deleteClientReport(id) {
  return await this.delete(`/client/reports/delete/${id}`);
}


// constant value for objection 
async getConstantValues() {
  return await this.get('/client/constants/active');
}



// generate astro mark report 

async generateAstroMark(data) {
  return await this.post("/v1/process", data);
}


// get AstroMark reports
getAstroMarkReports() {
  return this.get("/v1/Astro-list");
}


// manage customer add

async createManageCustomers(data) {
  return await this.post("/manage-customer/create", data);
}
// manage customer fetch
async getManageCustomers() {
  return await this.get("/manage-customer/list");
}
// manage customer and assigned trademark 
deleteManageCustomer(customerId) {
  return this.delete(`/manage-customer/delete/${customerId}`);
}

///
async assignCustomerTrademarks(data) {
 return await this.post("/manage-customer/assign-trademarks", data);
 
};
async bulkCreateCustomers(data) {
  return await this.post("/manage-customer/bulk-create", data);
}
// update full customer details with assigned trademark
async updateFullCustomer(data) {
 return await this.put("/manage-customer/update-full-customer", data);
 
};

// manage customer find by trademark no. in status change trademark
getCustomersByTrademark(appNo){

return this.get(`/manage-customer/trademark-customers/${appNo}`);
}

async bulkDeleteCustomers(data) {
  return await this.post("/manage-customer/bulk-delete", data);
}
sendEmailToClient(payload){
  //console.log("to client 👉", payload);

  return this.post(`/send-mail/client-customer-email`, payload);
}

sendEmailToCustomer(payload){
  return this.post(`/send-mail/client-customer-email`, payload);
}
sendJournalEMailToClient(payload){
  return this.post(`/send-mail/client-customer-sendJournalMail`, payload);
}
sendJournalEMailToCustomer(payload){
  return this.post(`/send-mail/client-customer-sendJournalMail`, payload);
}
sendStatusChangeTrackerEmailToClient(payload){
  return this.post(`/send-mail/client-customer-sendStatusChangeTrackerMail`, payload);
}
sendAttorneyChangeTrackerEmailToClient(payload){
  return this.post(`/send-mail/client-customer-sendAttorneyChangeTrackerMail`, payload);
}
sendUploadedDocumentChangeTrackerEmailToClient(payload){
  // console.log("sendUploadDocumentChangeTrackerEmailToClient",payload)
  return this.post(`/send-mail/client-customer-sendUploadedDocumentChangeTrackerMail`, payload);
}
sendApplicationLevelMailToClient(payload){
  return this.post(`/send-mail/client-customer-sendApplicationLevelMailToClient`, payload);
}
sendFocusWordSearchMailToClient(payload){
  return this.post(`/send-mail/client-customer-sendFocusWordSearchMailToClient`, payload);
}
sendWatchCombinationMail(payload){
  return this.post(`/send-mail/client-customer-sendWatchCombinationMail`, payload);
}
sendProprietorTrackerMail(payload){
  return this.post(`/send-mail/client-customer-sendProprietorTrackerMail`, payload);
}


sendExaminationEMailToClient(payload){
  //console.log("to client 👉", payload);

  return this.post(`/send-mail/client-customer-sendExaminationMail`, payload);
}

sendExaminationEMailToCustomer(payload){
  //console.log("to customer 👉", payload);
  return this.post(`/send-mail/client-customer-sendExaminationMail`, payload);
}
sendApplicationStatusMailToClient(payload){
  //console.log("to client 👉", payload);

  return this.post(`/send-mail/client-customer-sendApplicationStatusMail`, payload);
}

sendApplicationStatusMailToCustomer(payload){
  //console.log("to customer 👉", payload);
  return this.post(`/send-mail/client-customer-sendApplicationStatusMail`, payload);
}
sendManageTrademarkMailToClient(payload){
  //console.log("to client 👉", payload);
  return this.post(`/send-mail/client-customer-sendManageTrademarkMail`, payload);
}
sendManageTrademarkMailToCustomer(payload){
  //console.log("to customer 👉", payload);
  return this.post(`/send-mail/client-customer-sendManageTrademarkMail`, payload);
}




// trademark quota getting 
getTrademarkQuota(){

return this.get(`/quota/trademark-quota`);
}

// search report quota getting 

getSearchReportQuota(){

return this.get(`/quota/search-report-quota`);
}


// astro mark quota getting 
getAstroMarkReportQuota(){

return this.get(`/quota/astromark-report-quota`);
}
// legal draft quota getting 
getLegalDraftingQuota(){

return this.get(`/quota/legal-drafting-quota`);
}

getTMAvailabilityPredictorQuota(){

return this.get(`/quota/TM-availability-predictor-quota`);
}

// emails logs table data
getEmailAnalytics(){

return this.get(`/send-mail/email-analytics`);
}

//Proprietor data save in watch 
saveClientProprietors(proprietorsData){
  return this.post(`/proprietor-status`, proprietorsData);
}
//Get Proprietor data saved in watch 
// getClientProprietors(){ 
//   return this.get(`/proprietor-status`);
// }
// Get Proprietor tracker data saved in watch 
getProprietorData(){ 
  return this.get(`/proprietor-status/tracker`);
}
getClientProprietors({ page, page_size }) {
  return this.get('/proprietor-status', {
    page,
    page_size
  });
}
updateClientProprietorDate(id, data) {
  return this.put(`/proprietor-status/${id}`, data);
}

//Delete Proprietor data saved in watch 

deleteClientProprietor(id){
  return this.delete(`/proprietor-status/${id}`);
}
// Proprietor quota getting 
getProprietorQuota(){

return this.get(`/quota/proprietor-quota`);
}
fetchTrademarksByAttorney(code) { 

  return this.post(
    `/trademark/fetch-trademark-by-attorney-code`,
    {
      attorney_code: code  
    }
  );
}

async insertFocusWord(payload) {
  // console.log("searchTrademarks HIT", payload);
  return await this.post('/focus-word/insert-focus-word', payload);
}
async getFocusWord(payload) {
  // console.log("searchTrademarks HIT", payload);
  return await this.post('/focus-word/get-focus-word',payload);
}

addFocusWord(payload) {
  return this.post("/focus-word/add-focus-word", payload);
}
updateFocusWord(payload) {
  return this.put("/focus-word/update", payload);
}

deleteFocusWord(payload) {
  return this.delete("/focus-word/delete", { data: payload });
}

async convertImage(payload) {
  //console.log("convert image payload",payload)
  return await this.post("/image/convert-image", payload);
}
async createTrademarkTag(payload) {
    // console.log("createTrademarkTag payload",payload)

  return await this.post("/manage-tags/add-tag", payload);
}

async getTrademarkTags() {
  return await this.get("/manage-tags/get-tags");
}

async updateTrademarkTag(id, payload) {
  return await this.put(`/manage-tags/update-tag/${id}`, payload);
}

async deleteTrademarkTag(id) {
  return await this.delete(`/manage-tags/delete-tag/${id}`);
}

async assignTrademarkTag(payload) {
      //console.log("assignTrademarkTag payload",payload)

  return await this.post("/manage-tags/assign-tag", payload);
}


// ------------------wallet tab -----------
fetchUserDocuments(application_number) {
  return this.get(`/user-documents?application_number=${application_number}`);
}

uploadUserDocument(payload) {
  return this.post(`/user-documents/upload`, payload);
}

updateUserDocument(id, payload) {
  return this.put(`/user-documents/${id}`, payload);
}

deleteUserDocument(id) {
  return this.delete(`/user-documents/${id}`);
}

downloadUserDocument(id) {
  return this.get(`/user-documents/download/${id}`);
}

fetchDocumentCategories() {
  return this.get(`/user-documents/categories`);
}

  // ===== BRAND GENERATOR =====

  async getBrandGeneratorQuota() {
    return await this.get('/brand-generator/quota');
  }

  async generateBrand(data) {
    return await this.post('/brand-generator/generate', data);
  }

  // Poll job status — POST with request_id + original inputs so backend can store them on completion
  async checkBrandGeneratorStatus(requestId, { keywords, nature_of_business } = {}) {
    return await this.post('/brand-generator/status', {
      request_id: requestId,
      keywords:           keywords           || null,
      nature_of_business: nature_of_business || null,
    });
  }

  async getBrandGeneratorHistory(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return await this.get(`/brand-generator/history${qs ? '?' + qs : ''}`);
  }

    async getIncompleteGenerations() {
    return await this.get('/brand-generator/incomplete');
  }

  async saveBrandResult(data) {
    return await this.post('/brand-generator/save', data);
  }

  async deleteBrandResult(id) {
    return await this.delete(`/brand-generator/history/${id}`);
  }
  async checkBrandDomain(domain) {
    return await this.post('/brand-generator/check-domain', { domain });
  }
  async checkBrandMCA(name) {
    return await this.post('/brand-generator/check-mca', { name });
  }
  async checkBrandAppStore(name) {
    return await this.post('/brand-generator/check-appstore', { name });
  }
  async checkBrandWikipedia(name) {
    return await this.post('/brand-generator/check-wikipedia', { name });
  }
  async checkBrandTwitter(name) {
    return await this.post('/brand-generator/check-twitter', { name });
  }

  // ── TM Predictor History ──────────────────────────────────────────────────
  async getPredictorHistory()           { return this.get('/brand-generator/predictor/history'); }
  async savePredictorHistory(data)      { return this.post('/brand-generator/predictor/history', data); }
  async deletePredictorHistory(id)      { return this.delete(`/brand-generator/predictor/history/${id}`); }
  async searchBrandNames(data)          { return this.post('/brand-generator/search', data); }

  // ═══════════════════════════════════════════════════════════════════════════
  // CLIENT BILLING MODULE — /api/billing/*
  // Vertical-agnostic: works for Trademark, Patent, Copyright, Corporate, etc.
  // ═══════════════════════════════════════════════════════════════════════════

  // Firm config
  async getBillingFirmConfig() {
    return this.get('/billing/config');
  }
  async saveBillingFirmConfig(data) {
    return this.post('/billing/config', data);
  }

  // Dashboard
  async getBillingDashboard() {
    return this.get('/billing/dashboard');
  }
  // Items (master list)
  async getBillingItems(params) {
    return this.get('/billing/items', { params });
  }
  async createBillingItem(data) {
    return this.post('/billing/items', data);
  }
  async updateBillingItem(id, data) {
    return this.put(`/billing/items/${id}`, data);
  }
  async deleteBillingItem(id) {
    return this.delete(`/billing/items/${id}`);
  }
  // Customers
  async getBillingCustomers(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.get(`/billing/customers${qs ? '?' + qs : ''}`);
  }
  async getBillingCustomer(id) {
    return this.get(`/billing/customers/${id}`);
  }
  async getClientStatement(id, params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.get(`/billing/customers/${id}/statement${qs ? '?' + qs : ''}`);
  }
  async createBillingCustomer(data) {
    return this.post('/billing/customers', data);
  }
  async updateBillingCustomer(id, data) {
    return this.put(`/billing/customers/${id}`, data);
  }
  async deleteBillingCustomer(id) {
    return this.delete(`/billing/customers/${id}`);
  }
  // Retainer — aliases (kept for backward compatibility)
  async getRetainerAccount(customerId)      { return this.getBillingCustomerRetainer(customerId); }
  async depositRetainer(data)               { return this.depositBillingRetainer(data); }
  async getRetainerTransactions(customerId) { return this.getBillingRetainerTransactions(customerId); }

  // Matters
  async getBillingMatters(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.get(`/billing/matters${qs ? '?' + qs : ''}`);
  }
  async getBillingMatter(id) {
    return this.get(`/billing/matters/${id}`);
  }
  async createBillingMatter(data) {
    return this.post('/billing/matters', data);
  }
  async updateBillingMatter(id, data) {
    return this.put(`/billing/matters/${id}`, data);
  }
  async deleteBillingMatter(id) {
    return this.delete(`/billing/matters/${id}`);
  }

  // Entries
  async getBillingEntries(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.get(`/billing/entries${qs ? '?' + qs : ''}`);
  }
  async createBillingEntry(data) {
    return this.post('/billing/entries', data);
  }
  async updateBillingEntry(id, data) {
    return this.put(`/billing/entries/${id}`, data);
  }
  async deleteBillingEntry(id) {
    return this.delete(`/billing/entries/${id}`);
  }

  // Invoices
  async getBillingInvoices(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.get(`/billing/invoices${qs ? '?' + qs : ''}`);
  }
  async getBillingInvoice(id) {
    return this.get(`/billing/invoices/${id}`);
  }
  async createBillingInvoice(data) {
    return this.post('/billing/invoices', data);
  }
  async updateBillingInvoice(id, data) {
    return this.put(`/billing/invoices/${id}`, data);
  }
  async updateBillingInvoiceStatus(id, status) {
    return this.patch(`/billing/invoices/${id}/status`, { status });
  }
  async deleteBillingInvoice(id) {
    return this.delete(`/billing/invoices/${id}`);
  }
  async sendBillingInvoice(id, email) {
    return this.post(`/billing/invoices/${id}/send`, { email });
  }
  getBillingInvoicePdfUrl(id) {
    return `${this.api.defaults.baseURL}/billing/invoices/${id}/pdf`;
  }
  async getBillingInvoicePdf(id) {
    // Fetch PDF with auth headers (avoids the "Client access required" error on direct URL open)
    const response = await this.api.get(`/billing/invoices/${id}/pdf`, { responseType: 'blob' });
    return new Blob([response.data], { type: 'application/pdf' });
  }

  // Payments
  async getBillingPayments(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.get(`/billing/payments${qs ? '?' + qs : ''}`);
  }
  async recordBillingPayment(data) { return this.post('/billing/payments', data); }
  async updateBillingPayment(id, data) { return this.put(`/billing/payments/${id}`, data); }
  async deleteBillingPayment(id) { return this.delete(`/billing/payments/${id}`); }
  async getBillingCustomerRetainer(customerId) { return this.get(`/billing/customers/${customerId}/retainer`); }
  async getBillingRetainerTransactions(customerId) { return this.get(`/billing/customers/${customerId}/retainer/transactions`); }
  async depositBillingRetainer(data) { return this.post(`/billing/customers/${data.customer_id}/retainer/deposit`, data); }
  async applyRetainerToInvoice(customerId, data) { return this.post(`/billing/customers/${customerId}/retainer/apply`, data); }
  async refundBillingRetainer(customerId, data) { return this.post(`/billing/customers/${customerId}/retainer/refund`, data); }
  async getBillingRetainerSummary() { return this.get('/billing/retainer/summary'); }

  // Reports
  async getBillingReports(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.get(`/billing/reports${qs ? '?' + qs : ''}`);
  }
  // Recurring invoices
  async getBillingRecurring() { return this.get('/billing/recurring'); }
  async createBillingRecurring(data) { return this.post('/billing/recurring', data); }
  async updateBillingRecurring(id, data) { return this.put(`/billing/recurring/${id}`, data); }
  async deleteBillingRecurring(id) { return this.delete(`/billing/recurring/${id}`); }
  async runBillingRecurring(id) { return this.post(`/billing/recurring/${id}/run`, {}); }

  // ── Proposals ────────────────────────────────────────────────────────────
  async getBillingProposals(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.get(`/billing/proposals${qs ? '?' + qs : ''}`);
  }
  async getBillingProposal(id)           { return this.get(`/billing/proposals/${id}`); }
  async createBillingProposal(data)      { return this.post('/billing/proposals', data); }
  async updateBillingProposal(id, data)  { return this.put(`/billing/proposals/${id}`, data); }
  async deleteBillingProposal(id)        { return this.delete(`/billing/proposals/${id}`); }
  async convertProposalToInvoice(id, data) { return this.post(`/billing/proposals/${id}/convert`, data || {}); }
  async getBillingProposalPdf(id) {
    const response = await this.api.get(`/billing/proposals/${id}/pdf`, { responseType: 'blob' });
    return new Blob([response.data], { type: 'application/pdf' });
  }
  async sendBillingProposalByEmail(id, data) { return this.post(`/billing/proposals/${id}/send`, data || {}); }

  // ── Opponent Strength Meter ───────────────────────────────────────────────
  async getOpponentStrengthQuota()            { return this.get('/opponent-strength/quota'); }
  async searchOpponents(data)                 { return this.post('/opponent-strength/search', data); }
  async getOpponentWatchlist()                { return this.get('/opponent-strength/list'); }
  async addOpponentToWatchlist(data)          { return this.post('/opponent-strength/add', data); }
  async removeOpponentFromWatchlist(id)       { return this.delete(`/opponent-strength/list/${id}`); }
  async getOpponentAnalysis()                 { return this.get('/opponent-strength/analysis'); }



// Upload file to OpenAI via backend (for legal drafting context PDFs)
async uploadFileToOpenAI(formData, onProgress = null) {
  return await this.upload('/legal-drafting/openai/files', formData, onProgress);
}

async getEmailAccounts() {
  return await this.get('/email-settings/list');
}

async createEmailAccount(data) {
  return await this.post('/email-settings/create', data);
}

async updateEmailAccount(id, data) {
  return await this.put(`/email-settings/update/${id}`, data);
}

async deleteEmailAccount(id) {
  return await this.delete(`/email-settings/delete/${id}`);
}

async sendTestEmail(data) {
  return await this.post('/email-settings/test-email', data);
}

async getDefaultEmailSettings() {
  return await this.get('/email-settings/default-settings');
}

/* ==========================================
   CLIENT EMAIL TEMPLATE
========================================== */

async getClientEmailTemplates() {
  return await this.get("/client-email-template/list");
}

async getClientEmailTemplate(id) {
  return await this.get(`/client-email-template/${id}`);
}

async updateClientEmailTemplate(id, data) {
  return await this.put(`/client-email-template/update/${id}`, data);
}

async resetClientEmailTemplate(id) {
  return await this.post(`/client-email-template/reset/${id}`);
}
uploadEmailLogo(formData) {
    return this.post(
        "/client-email-template/client-upload-logo",
        formData,
        {
            headers: {
                "Content-Type": "multipart/form-data"
            }
        }
    );
}

getEmailLogo() {
    return this.get("/client-email-template/client-logo");
}
removeEmailLogo() {
    return this.delete("/client-email-template/client-logo");
}

createMasterPlaceholders() {
    return this.post(
        "/client-email-template/master-placeholder/create"
    );
}

getMasterPlaceholderList() {
    return this.get(
        "/client-email-template/master-placeholder/list"
    );
}
updateMasterPlaceholders(placeholders) {
    return this.put(
        "/client-email-template/master-placeholder/update",
        {
            placeholders
        }
    );
}
//favourites----------
getFavourites(tabType) {
  return this.get(`/client/favourites/${tabType}`);
}

toggleFavourite(tabType, applicationNumber) {
  return this.post(`/client/favourites/${tabType}/toggle`, {
    application_number: applicationNumber,
  });
}
}
// Create and export singleton instance
const apiService = new ApiService();
export default apiService;